import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, IconButton } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

interface InlineEditProps {
  value: string;
  onSave: (v: string) => void;
  variant?: any;
  bold?: boolean;
  placeholder?: string;
  fontSize?: string;
}

export default function InlineEdit({
  value,
  onSave,
  variant = 'body2',
  bold = false,
  placeholder = 'Enter text…',
  fontSize = '0.9rem',
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => { onSave(draft.trim() || value); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
        <TextField
          inputRef={inputRef}
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
          placeholder={placeholder}
          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize, py: 0.5 } }}
          variant="outlined"
        />
        <IconButton size="small" color="success" onClick={commit} sx={{ p: 0.25 }}>
          <SaveIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton size="small" onClick={cancel} sx={{ p: 0.25 }}>
          <CancelIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Typography
      variant={variant}
      sx={{
        fontWeight: bold ? 600 : 400,
        cursor: 'text',
        flex: 1,
        fontSize,
        '&:hover': { textDecoration: 'underline dotted' },
      }}
      onClick={() => { setDraft(value); setEditing(true); }}
      title="Click to edit"
    >
      {value || <span style={{ color: '#9E9E9E', fontSize }}>{placeholder}</span>}
    </Typography>
  );
}